import { parse, Allow } from 'partial-json';
import { BACKEND_URL } from './backend';

const active = new Map();
const PREFIX = 'rashi-v3:';

function cacheKey(payload, owner, profile) {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year:'numeric', month:'2-digit', day:'2-digit' }).format(new Date());
  return PREFIX + JSON.stringify([owner, payload, day, profile?.date_of_birth, profile?.time_of_birth, profile?.place_of_birth]);
}

export function requestReading(payload, { owner, profile, onProgress }) {
  const key = cacheKey(payload, owner, profile);
  let cached;
  try { cached = owner ? JSON.parse(sessionStorage.getItem(key) || 'null') : null; } catch { cached = null; }
  if (cached?.provider === 'gemini' && cached?.categories && cached.rashi === payload.rashi) return { cached, promise: Promise.resolve(cached), unsubscribe: () => {} };
  let job = active.get(key);
  if (!job) {
    job = { listeners: new Set(), last: null };
    const notify = value => { job.last = value; job.listeners.forEach(listener => listener(value)); };
    job.promise = streamReading(payload, notify).then(result => {
      if (owner) {
        try {
          const keys = Object.keys(sessionStorage).filter(k => k.startsWith(PREFIX));
          keys.slice(0, Math.max(0,keys.length-19)).forEach(k => sessionStorage.removeItem(k));
          sessionStorage.setItem(key, JSON.stringify(result));
        } catch { /* Reading remains usable when storage is unavailable. */ }
      }
      return result;
    }).finally(() => active.delete(key));
    active.set(key, job);
  }
  if (onProgress) { job.listeners.add(onProgress); if (job.last) onProgress(job.last); }
  return { cached: null, promise: job.promise, unsubscribe: () => job.listeners.delete(onProgress) };
}

async function streamReading(payload, notify) {
  const token = localStorage.getItem('auth_token');
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 100000);
  let reader;
  try {
    const response = await fetch(`${BACKEND_URL}/api/astrology/prediction/stream`, { method:'POST', credentials:'include', signal:controller.signal,
      headers:{ 'Content-Type':'application/json', ...(token ? { Authorization:`Bearer ${token}` } : {}) }, body:JSON.stringify(payload) });
    if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(typeof error.detail === 'string' ? error.detail : 'Unable to open this reading. Please try again.'); }
    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = '', draft = '', result = null;
    while (true) {
      const { done, value } = await reader.read();
      pending += done ? decoder.decode() : decoder.decode(value, { stream:true });
      let boundary;
      while ((boundary = pending.indexOf('\n\n')) >= 0) {
        const block = pending.slice(0,boundary); pending = pending.slice(boundary+2);
        const type = block.split('\n').find(line => line.startsWith('event: '))?.slice(7);
        const encoded = block.split('\n').filter(line => line.startsWith('data: ')).map(line => line.slice(6)).join('\n');
        if (!encoded) continue;
        const data = JSON.parse(encoded);
        if (type === 'error') throw new Error(data.message);
        if (type === 'status') notify({ status:data.message });
        if (type === 'delta') {
          draft += data.text;
          try { const partial = parse(draft.replace(/^\s*```(?:json)?\s*/i,''), Allow.STR | Allow.OBJ | Allow.ARR); notify({ title:typeof partial.title === 'string' ? partial.title : '', content:typeof partial.content === 'string' ? partial.content : '', status:'Writing your reading…' }); } catch { /* Incomplete JSON keys are expected between tokens. */ }
        }
        if (type === 'result') result = data;
      }
      if (result) return result;
      if (done) break;
    }
    throw new Error('The reading was interrupted. Please try again.');
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('This reading is taking longer than expected. Please try again.');
    throw error;
  } finally { clearTimeout(timeout); if (reader) { await reader.cancel().catch(() => {}); reader.releaseLock(); } }
}